import { useState } from "react";
import { useTranslation } from "react-i18next";
import { FACET_NULL } from "@ckgo2/shared/types";
import { useFacetedParams } from "@/hooks/useFacetedParams";
import { orderFacetBuckets, type FacetOrder } from "./facetOrder";

export type { FacetOrder } from "./facetOrder";

export interface FacetBucket {
  value: string | null;
  count: number;
}

interface FacetPanelProps {
  title: string;
  /** URL param + API facet key, e.g. "status". */
  paramKey: string;
  buckets: FacetBucket[];
  /** Map a raw value to a display label (status code → "Call back", userId → name). */
  labelFor?: (value: string) => string;
  /** Label for the null bucket (e.g. "Unassigned"); omit to hide it. */
  nullLabel?: string;
  /**
   * Kolik hodnot ukázat, než se skupina sbalí za „Zobrazit vše". Katalogové facety
   * (země) mají klidně 70 hodnot — bez tohohle by panel filtrů byl na tři obrazovky.
   */
  maxVisible?: number;
  /**
   * Pořadí hodnot (`orderFacetBuckets`): `"count"` (výchozí) podle počtu, `"name"` česky podle
   * vykresleného popisku, `{ enum }` v pořadí výčtu. U `"name"` a `{ enum }` je „bez hodnoty"
   * poslední.
   */
  order?: FacetOrder;
}

/**
 * One faceted filter group with live counts. Multi-select: clicking toggles the
 * value in the URL (`useFacetedParams`). Buckets sort by `order` (default: count
 * desc); the null bucket (rendered via `nullLabel`) maps to the shared `FACET_NULL`
 * sentinel so the API translates it to `IS NULL`.
 */
export default function FacetPanel({
  title,
  paramKey,
  buckets,
  labelFor,
  nullLabel,
  maxVisible = 8,
  order = "count",
}: FacetPanelProps) {
  const { t } = useTranslation();
  const { getValues, toggleValue } = useFacetedParams();
  const [expanded, setExpanded] = useState(false);
  const selected = getValues(paramKey);

  const rows = orderFacetBuckets(
    buckets.filter((b) => b.value !== null || nullLabel),
    order,
    labelFor,
  );

  if (rows.length === 0) return null;

  // Vybrané hodnoty musí být vidět vždy, i když by se do zkráceného seznamu nevešly —
  // jinak by filtr tiše platil a nešel odkliknout.
  const isSelected = (b: FacetBucket) => selected.includes(b.value === null ? FACET_NULL : b.value);
  const collapsible = rows.length > maxVisible;
  const visibleRows =
    !collapsible || expanded ? rows : rows.filter((b, i) => i < maxVisible || isSelected(b));

  return (
    <div className="border-b border-gray-100 py-4 last:border-0 dark:border-gray-700/60">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">{title}</h3>
      <ul className={`space-y-0.5 ${expanded && collapsible ? "max-h-72 overflow-y-auto" : ""}`}>
        {visibleRows.map((b) => {
          const isNull = b.value === null;
          const value = isNull ? FACET_NULL : b.value!;
          const label = isNull ? nullLabel! : labelFor ? labelFor(b.value!) : b.value!;
          const active = selected.includes(value);
          return (
            <li key={value}>
              <button
                type="button"
                onClick={() => toggleValue(paramKey, value)}
                className={`flex w-full items-center justify-between gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors ${
                  active
                    ? "bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300"
                    : "text-gray-600 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-gray-700/40"
                }`}
              >
                <span className="flex items-center gap-2 truncate">
                  <span
                    className={`flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded border ${
                      active
                        ? "border-primary-500 bg-primary-500 text-white"
                        : "border-gray-300 dark:border-gray-600"
                    }`}
                  >
                    {active && (
                      <svg viewBox="0 0 12 12" className="h-2.5 w-2.5" fill="none">
                        <path
                          d="M2.5 6.5L5 9l4.5-5"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                  </span>
                  <span className="truncate">{label}</span>
                </span>
                <span className="shrink-0 text-xs tabular-nums text-gray-400">{b.count}</span>
              </button>
            </li>
          );
        })}
      </ul>
      {collapsible && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="text-primary-600 dark:text-primary-400 mt-1 px-2 text-xs hover:underline"
        >
          {expanded
            ? t("shared.facetedList.showLess")
            : t("shared.facetedList.showAll", { count: rows.length })}
        </button>
      )}
    </div>
  );
}
